import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GlobalRecentSubset } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';

export function WhereYouLeftOff({ recentSubsets }: { recentSubsets: GlobalRecentSubset[] }) {
  if (recentSubsets.length === 0) {
    return null;
  }

  return (
    <div className="mb-8 space-y-2.5">
      <p className="ml-1.5 flex items-center gap-1.5 text-gray-500">
        <Icon className="lucide--hourglass text-xs text-gray-400" /> Recent Matters and Subsets
      </p>

      <div className="recents-scroll-fade flex items-center justify-start gap-2 overflow-x-auto">
        {/* Scroll-driven fades: right edge revealed near the end of the content, left edge appears once scrolled away from the start. */}
        <style>{`
        @property --recents-fade {
          syntax: '<length>';
          inherits: false;
          initial-value: 128px;
        }
        @property --recents-fade-start {
          syntax: '<length>';
          inherits: false;
          initial-value: 0px;
        }
        .recents-scroll-fade {
          /* Base (no overflow / unsupported browsers): no fade. When the container is
             not scrollable the scroll timeline is inactive, so the animation below never
             applies and this 0px base is used — avoiding a fade with nothing to scroll. */
          --recents-fade: 0px;
          --recents-fade-start: 0px;
          scrollbar-width: none;
          mask-image: linear-gradient(
            to right,
            transparent,
            #000 var(--recents-fade-start),
            #000 calc(100% - var(--recents-fade)),
            transparent
          );
        }
        .recents-scroll-fade::-webkit-scrollbar {
          display: none;
        }
        @supports (animation-timeline: scroll()) {
          .recents-scroll-fade {
            animation: recents-unfade linear both, recents-fade-in-start linear both;
            animation-timeline: scroll(self inline), scroll(self inline);
            animation-range: 95% 100%, 0% 5%;
          }
          @keyframes recents-unfade {
            from {
              --recents-fade: 128px;
            }
            to {
              --recents-fade: 0px;
            }
          }
          @keyframes recents-fade-in-start {
            from {
              --recents-fade-start: 0px;
            }
            to {
              --recents-fade-start: 128px;
            }
          }
        }
      `}</style>
        {recentSubsets.map((subset) => {
          const isAllDocuments = subset.case.isAllDocuments;
          const subPath = isAllDocuments ? '' : `/subsets/${subset.caseId}`;
          const linkTo = `/matters/${subset.case.matterId}${subPath}`;

          return (
            <Link
              key={subset.caseId}
              to={linkTo}
              onClick={() =>
                trackCustomEvent(TrackingEventsV2.NavigationClickRecentSubset, {
                  matterId: subset.case.matterId,
                  subsetId: subset.caseId,
                })
              }
            >
              <div className="group relative h-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2 whitespace-nowrap duration-150 hover:border-gray-300">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 shrink items-center gap-2">
                    {isAllDocuments ? (
                      <>
                        <Icon className="lucide--briefcase shrink-0 text-sm text-teal-500" />
                        <h3 className="truncate text-sm font-medium text-gray-700">{subset.case.matter.name}</h3>
                      </>
                    ) : (
                      <>
                        <Icon className="lucide--group shrink-0 text-sm text-sky-500" />
                        <h3 className="truncate text-sm font-medium text-gray-700">{subset.case.name}</h3>
                        <span className="size-0.75 rounded-full bg-gray-300"></span>
                        <p className="text-gray-500">{subset.case.matter.name}</p>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <span className="text-xs text-gray-400">{format(subset.accessedAt, 'dd MMMM')}</span>
                    <Icon className="lucide--chevron-right text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-600" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
